import React, { useState, useEffect } from 'react';
import { Card, Button, TextInput, Label, Checkbox, Spinner, Accordion, Table } from 'flowbite-react';
import { observer } from 'mobx-react-lite';
import { HiSave, HiPlus, HiTrash } from 'react-icons/hi';
import { tenantStore } from '../stores/tenantStore';
import { toastStore } from '../stores/toastStore';
import type { ScreenUploadMonitoring, DayTimeRestriction } from '../types';

interface MonitoringSettingsTabProps {
  tenantId: string;
}

const MonitoringSettingsTab: React.FC<MonitoringSettingsTabProps> = observer(({ tenantId }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [screenUploadMonitoring, setScreenUploadMonitoring] = useState<ScreenUploadMonitoring>({
    enabled: false,
    slackWebhookUrl: '',
    noUploadCheck: false,
    thresholdMinutes: 5,
    monitoredScreenCount: 1,
  });

  // Initialize with 7 empty arrays (one for each day of the week)
  const [monitoringTimeRestrictions, setMonitoringTimeRestrictions] = useState<DayTimeRestriction[][]>(
    Array.from({ length: 7 }, () => [])
  );

  useEffect(() => {
    loadSettings();
  }, [tenantId]);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const config = await tenantStore.loadMonitoringSettings(tenantId);
      if (config.screenUploadMonitoring) {
        setScreenUploadMonitoring(config.screenUploadMonitoring);
      }
      if (config.monitoringTimeRestrictions) {
        setMonitoringTimeRestrictions(config.monitoringTimeRestrictions);
      }
    } catch (err) {
      toastStore.showError('監視設定の読み込みに失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await tenantStore.updateMonitoringSettings(tenantId, {
        screenUploadMonitoring,
        monitoringTimeRestrictions,
      });
      toastStore.showSuccess('監視設定を保存しました');
    } catch (err) {
      toastStore.showError('監視設定の保存に失敗しました');
    } finally {
      setIsSaving(false);
    }
  };

  // Add a new time restriction for a specific day
  const handleAddTimeRestriction = (dayIndex: number) => {
    const newRestrictions = [...monitoringTimeRestrictions];
    if (!newRestrictions[dayIndex]) {
      newRestrictions[dayIndex] = [];
    }
    newRestrictions[dayIndex].push({
      enabled: true,
      allDay: false,
      startTime: '12:00',
      endTime: '13:00',
    });
    setMonitoringTimeRestrictions(newRestrictions);
  };

  // Remove a time restriction for a specific day
  const handleRemoveTimeRestriction = (dayIndex: number, restrictionIndex: number) => {
    const newRestrictions = [...monitoringTimeRestrictions];
    newRestrictions[dayIndex].splice(restrictionIndex, 1);
    setMonitoringTimeRestrictions(newRestrictions);
  };

  // Update a time restriction for a specific day
  const handleUpdateTimeRestriction = (
    dayIndex: number,
    restrictionIndex: number,
    field: keyof DayTimeRestriction,
    value: string | boolean
  ) => {
    const newRestrictions = [...monitoringTimeRestrictions];
    if (!newRestrictions[dayIndex]) {
      newRestrictions[dayIndex] = [];
    }
    newRestrictions[dayIndex][restrictionIndex] = {
      ...newRestrictions[dayIndex][restrictionIndex],
      [field]: value,
    };
    setMonitoringTimeRestrictions(newRestrictions);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <h3 className="text-lg font-semibold mb-4">スクリーンアップロード監視</h3>

        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Checkbox
              id="monitoring-enabled"
              checked={screenUploadMonitoring.enabled}
              onChange={(e) =>
                setScreenUploadMonitoring({
                  ...screenUploadMonitoring,
                  enabled: e.target.checked,
                })
              }
              disabled={isSaving}
            />
            <Label htmlFor="monitoring-enabled">
              通知時間帯の監視を行う
            </Label>
          </div>

          <p className="text-sm text-gray-500">
            有効にすると、通知時間帯（曜日別通知時間帯設定）内でIoTデバイスからの画像アップロードを監視します。
          </p>

          <div>
            <Label htmlFor="slack-webhook-url">Slack Incoming Webhook URL</Label>
            <TextInput
              id="slack-webhook-url"
              type="url"
              placeholder="https://hooks.slack.com/services/..."
              value={screenUploadMonitoring.slackWebhookUrl || ''}
              onChange={(e) =>
                setScreenUploadMonitoring({
                  ...screenUploadMonitoring,
                  slackWebhookUrl: e.target.value,
                })
              }
              disabled={isSaving || !screenUploadMonitoring.enabled}
            />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="no-upload-check"
              checked={screenUploadMonitoring.noUploadCheck}
              onChange={(e) =>
                setScreenUploadMonitoring({
                  ...screenUploadMonitoring,
                  noUploadCheck: e.target.checked,
                })
              }
              disabled={isSaving || !screenUploadMonitoring.enabled}
            />
            <Label htmlFor="no-upload-check">
              指定時間画像がアップロードされていなければ通知する
            </Label>
          </div>

          <div>
            <Label htmlFor="threshold-minutes">閾値（分）</Label>
            <TextInput
              id="threshold-minutes"
              type="number"
              min="1"
              value={screenUploadMonitoring.thresholdMinutes}
              onChange={(e) =>
                setScreenUploadMonitoring({
                  ...screenUploadMonitoring,
                  thresholdMinutes: parseInt(e.target.value, 10) || 5,
                })
              }
              disabled={isSaving || !screenUploadMonitoring.enabled || !screenUploadMonitoring.noUploadCheck}
              className="w-32"
            />
            <p className="text-sm text-gray-500 mt-1">
              この時間（分）以上画像がアップロードされていない場合に通知します（デフォルト: 5分）
            </p>
          </div>

          <div>
            <Label htmlFor="monitored-screen-count">監視対象スクリーン数</Label>
            <TextInput
              id="monitored-screen-count"
              type="number"
              min="1"
              value={screenUploadMonitoring.monitoredScreenCount}
              onChange={(e) =>
                setScreenUploadMonitoring({
                  ...screenUploadMonitoring,
                  monitoredScreenCount: parseInt(e.target.value, 10) || 1,
                })
              }
              disabled={isSaving || !screenUploadMonitoring.enabled || !screenUploadMonitoring.noUploadCheck}
              className="w-32"
            />
            <p className="text-sm text-gray-500 mt-1">
              監視するスクリーンの数（ScreenNumber 1からN）を指定します（デフォルト: 1）
            </p>
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <Button onClick={handleSave} disabled={isSaving}>
            <HiSave className="mr-2 h-4 w-4" />
            {isSaving ? '保存中...' : '設定を保存'}
          </Button>
        </div>
      </Card>

      <Card>
        <h3 className="text-lg font-semibold mb-4">監視しない時間帯</h3>
        <p className="text-sm text-gray-500 mb-4">
          各曜日に複数の時間帯を指定できます。昼休みや夜間休診など、IoTデバイスの電源が切れている時間帯を設定してください。
          この設定は上記の「スクリーンアップロード監視」とは独立しており、監視処理自体をスキップします。
          また、テナント内部の設定「曜日別通知時間帯設定」の範囲外では、通知関連の監視を行っていないため、該当の時間帯も監視通知されません。
        </p>

        <Accordion>
          {['日曜日', '月曜日', '火曜日', '水曜日', '木曜日', '金曜日', '土曜日'].map((dayLabel, dayIndex) => (
            <Accordion.Panel key={dayIndex}>
              <Accordion.Title>{dayLabel}</Accordion.Title>
              <Accordion.Content>
                <div className="space-y-4">
                  {monitoringTimeRestrictions[dayIndex]?.length === 0 ? (
                    <p className="text-sm text-gray-500">この曜日には時間帯が設定されていません</p>
                  ) : (
                    <Table>
                      <Table.Head>
                        <Table.HeadCell>有効</Table.HeadCell>
                        <Table.HeadCell>24時間スキップ</Table.HeadCell>
                        <Table.HeadCell>開始時刻</Table.HeadCell>
                        <Table.HeadCell>終了時刻</Table.HeadCell>
                        <Table.HeadCell>操作</Table.HeadCell>
                      </Table.Head>
                      <Table.Body className="divide-y">
                        {monitoringTimeRestrictions[dayIndex]?.map((restriction, restrictionIndex) => (
                          <Table.Row key={restrictionIndex} className="bg-white dark:border-gray-700 dark:bg-gray-800">
                            <Table.Cell>
                              <Checkbox
                                checked={restriction.enabled}
                                onChange={(e) =>
                                  handleUpdateTimeRestriction(dayIndex, restrictionIndex, 'enabled', e.target.checked)
                                }
                                disabled={isSaving}
                              />
                            </Table.Cell>
                            <Table.Cell>
                              <Checkbox
                                checked={restriction.allDay}
                                onChange={(e) =>
                                  handleUpdateTimeRestriction(dayIndex, restrictionIndex, 'allDay', e.target.checked)
                                }
                                disabled={isSaving || !restriction.enabled}
                              />
                            </Table.Cell>
                            <Table.Cell>
                              <TextInput
                                type="time"
                                value={restriction.startTime}
                                onChange={(e) =>
                                  handleUpdateTimeRestriction(dayIndex, restrictionIndex, 'startTime', e.target.value)
                                }
                                disabled={isSaving || !restriction.enabled || restriction.allDay}
                                className="w-32"
                              />
                            </Table.Cell>
                            <Table.Cell>
                              <TextInput
                                type="time"
                                value={restriction.endTime}
                                onChange={(e) =>
                                  handleUpdateTimeRestriction(dayIndex, restrictionIndex, 'endTime', e.target.value)
                                }
                                disabled={isSaving || !restriction.enabled || restriction.allDay}
                                className="w-32"
                              />
                            </Table.Cell>
                            <Table.Cell>
                              <Button
                                size="xs"
                                color="failure"
                                onClick={() => handleRemoveTimeRestriction(dayIndex, restrictionIndex)}
                                disabled={isSaving}
                              >
                                <HiTrash className="h-4 w-4" />
                              </Button>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  )}

                  <Button
                    size="sm"
                    color="light"
                    onClick={() => handleAddTimeRestriction(dayIndex)}
                    disabled={isSaving}
                  >
                    <HiPlus className="mr-2 h-4 w-4" />
                    時間帯を追加
                  </Button>
                </div>
              </Accordion.Content>
            </Accordion.Panel>
          ))}
        </Accordion>

        <div className="flex justify-end mt-6">
          <Button onClick={handleSave} disabled={isSaving}>
            <HiSave className="mr-2 h-4 w-4" />
            {isSaving ? '保存中...' : '設定を保存'}
          </Button>
        </div>
      </Card>
    </div>
  );
});

export default MonitoringSettingsTab;
